import { roundToTwoDecimals } from '@fruitful/fruitils';

import { PortfolioCalculator } from '../portfolio/PortfolioCalculator';
import { CORE_8_PORTFOLIO } from '../portfolio/portfolios/Core8Portfolio';
import type { CalculatedPortfolioReturns } from '../portfolio/types';

export class InvestmentGrowthSimulator {
    private readonly INFLATION_RATE = 0.025;
    private readonly ANNUAL_EQUITIES_VOLATILITY = 0.18;
    private readonly ANNUAL_FIXED_INCOME_VOLATILITY = 0.05;
    private readonly EQUITIES_FIXED_INCOME_CORRELATION = 0.1;

    constructor(
        private readonly currentValue: number,
        private readonly equitiesPercentage: number,
    ) {}

    public getExpectedAnnualGrowth(): number {
        // Base the nominal returns off of the default portfolio.
        const nominalReturns = this.getNominalPortfolioReturns();

        const equitiesRealReturn =
            nominalReturns.equities - this.INFLATION_RATE;
        const fixedIncomeRealReturn =
            nominalReturns.fixedIncome - this.INFLATION_RATE;

        const expectedReturn =
            this.equitiesPercentage * equitiesRealReturn +
            (1 - this.equitiesPercentage) * fixedIncomeRealReturn;

        return roundToTwoDecimals(this.currentValue * expectedReturn);
    }

    public simulateAnnualGrowth(): number {
        const mean = this.getExpectedAnnualGrowth();
        const volatilityPercentage = this.getPortfolioVolatility();
        const volatilityDollars = this.currentValue * volatilityPercentage;

        const simulatedGrowth = this.simulateRandomNormal(
            mean,
            volatilityDollars,
        );

        return roundToTwoDecimals(simulatedGrowth);
    }

    public getPortfolioVolatility(): number {
        const volI = this.ANNUAL_EQUITIES_VOLATILITY;
        const volJ = this.ANNUAL_FIXED_INCOME_VOLATILITY;

        const wI = this.equitiesPercentage;
        const wJ = 1 - this.equitiesPercentage;

        // Portfolio variance formula with correlation
        const variance =
            wI * wI * volI * volI +
            wJ * wJ * volJ * volJ +
            2 * wI * wJ * volI * volJ * this.EQUITIES_FIXED_INCOME_CORRELATION;

        return Math.sqrt(variance);
    }

    /**
     * TODO - Using the weighted average of the Core 8 portfolio as default.
     * Get the real (inflation-adjusted) portfolio returns
     * @returns Object containing real returns for equities and fixed income
     */
    private getNominalPortfolioReturns(): CalculatedPortfolioReturns {
        return PortfolioCalculator.calculateReturns(CORE_8_PORTFOLIO);
    }

    private simulateRandomNormal(mean = 0, stdDev = 1): number {
        let u = 0;
        let v = 0;
        while (u === 0) u = Math.random(); // Avoid 0
        while (v === 0) v = Math.random();
        const z = Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
        return z * stdDev + mean;
    }
}
